import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import {
  Archive,
  CalendarCheck,
  ClipboardCheck,
  ListChecks,
  Inbox,
  LibraryBig,
  LineChart,
  ListTodo,
  Repeat,
  Rows3,
  Settings,
  Sparkles,
  CircleHelp,
} from 'lucide-react'
import { api } from '@convex/_generated/api'
import { CaptureDialog } from '@/features/inbox/CaptureDialog'
import { InboxShortcut } from '@/features/inbox/InboxShortcut'
import { TodayShortcut } from '@/features/planner/TodayShortcut'
import { GoToShortcut } from '@/features/navigation/GoToShortcut'
import { SearchDialog } from '@/features/search/SearchDialog'
import { UndoNotice } from '@/components/UndoNotice'
import { SaveNotice } from './SaveNotice'
import { UpdateNotice } from './UpdateNotice'
import { formatVersion, versionTitle } from '@/lib/version'

/**
 * Five destinations, not ten.
 *
 * Five of the ten were object types — Tasks, Projects, Chores, Habits,
 * Routines — which is navigation that mirrors the schema rather than the
 * reason you opened the app, and ten of them measured about 380px against a
 * 360px phone. They live in the Library now; every route still exists and
 * every link still works.
 *
 */
/**
 * A phone's five, and a sidebar's eleven.
 *
 * They are different lists rather than one list rendered twice, because the
 * constraint that shaped the five does not exist on a sidebar. Five is what
 * fits a 320px bar with legible labels, so a phone gets the daily path —
 * Today, Inbox, Tasks — plus Library as the way to everything else.
 *
 * A sidebar has room for the everything else, and an index page in front of it
 * is then pure interposition: a screen whose only job is to list links you
 * could have been shown directly. So the desktop list is flat and Library does
 * not appear in it at all.
 */
const MOBILE_NAV = [
  { to: '/', label: 'Today', icon: CalendarCheck },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/tasks', label: 'Tasks', icon: ListTodo },
  { to: '/library', label: 'Library', icon: LibraryBig },
  { to: '/settings', label: 'Settings', icon: Settings },
] as const

const DESKTOP_NAV = [
  { to: '/', label: 'Today', icon: CalendarCheck },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/tasks', label: 'Tasks', icon: ListTodo },
  { to: '/habits', label: 'Habits', icon: Repeat },
  { to: '/routines', label: 'Routines', icon: Rows3 },
  { to: '/chores', label: 'Chores', icon: Sparkles },
  { to: '/lists', label: 'Lists', icon: ListChecks },
  { to: '/review', label: 'Review', icon: ClipboardCheck },
  { to: '/insights', label: 'Insights', icon: LineChart },
  { to: '/archive', label: 'Archive', icon: Archive },
  { to: '/help', label: 'Help', icon: CircleHelp },
  { to: '/settings', label: 'Settings', icon: Settings },
] as const

/**
 * Mobile-first shell. Phones get a bottom tab bar (thumb reach, safe-area
 * aware); from `sm` up it becomes Todoist's warm off-white sidebar, where the
 * active item is a soft red-tinted row rather than a hard highlight.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const board = useQuery(api.board.today, {})
  const inboxCount = board?.inboxCount ?? 0

  return (
    <div className="min-h-dvh sm:flex">
      <CaptureDialog />
      <SearchDialog />
      <InboxShortcut />
      <TodayShortcut />
      <GoToShortcut />
      <SaveNotice />
      <UndoNotice />
      <UpdateNotice />

      <nav
        aria-label="Main"
        className="bg-sidebar/95 border-sidebar-border pb-safe pl-safe pr-safe sm:pt-safe fixed inset-x-0 bottom-0 z-10 border-t backdrop-blur sm:sticky sm:top-0 sm:flex sm:h-dvh sm:w-60 sm:shrink-0 sm:flex-col sm:border-t-0 sm:border-r"
      >
        {/* The phone bar: five columns sharing the width, no scroll.

            It used to be a scrolling strip of fixed-width items because there
            were more of them than fitted — and a bar that scrolls without
            saying so simply hides its last entries. Five divides a 320px
            screen into legible columns. */}
        <ul className="flex sm:hidden">
          {MOBILE_NAV.map(({ to, label, icon: Icon }) => {
            const badge = to === '/inbox' && inboxCount > 0 ? inboxCount : null

            return (
              <li key={to} className="min-w-0 flex-1">
                <Link
                  to={to}
                  activeOptions={{ exact: to === '/' }}
                  className="text-muted-foreground hover:text-foreground data-[status=active]:text-primary flex min-h-14 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors"
                >
                  {/* The icon owns its own positioning context so the count
                      hangs off its corner instead of covering it. */}
                  <span className="relative shrink-0">
                    <Icon className="size-[18px]" aria-hidden />
                    {badge !== null && (
                      <Badge
                        count={badge}
                        className="absolute -top-2 -right-2.5"
                      />
                    )}
                  </span>
                  <span>{label}</span>
                </Link>
              </li>
            )
          })}
        </ul>

        {/* The sidebar: everything, flat.

            No Library entry and no grouping. An index page in front of screens
            a sidebar has room to list directly is pure interposition — a screen
            whose only job is to name links you could have been shown. Library
            still exists as a route, because on a phone it is the way to the
            six destinations the bar cannot hold. */}
        <ul className="hidden sm:mt-4 sm:flex sm:flex-col sm:gap-0.5 sm:px-2">
          {DESKTOP_NAV.map(({ to, label, icon: Icon }) => {
            const badge = to === '/inbox' && inboxCount > 0 ? inboxCount : null

            return (
              <li key={to}>
                <Link
                  to={to}
                  activeOptions={{ exact: to === '/' }}
                  className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground data-[status=active]:text-primary data-[status=active]:bg-primary/8 flex min-h-9 items-center gap-3 rounded-md px-2.5 text-sm transition-colors data-[status=active]:font-medium"
                >
                  <Icon className="size-[18px] shrink-0" aria-hidden />
                  <span className="flex-1">{label}</span>
                  {badge !== null && <Badge count={badge} className="" />}
                </Link>
              </li>
            )
          })}
        </ul>

        {/* Bottom of the sidebar, and quiet enough to ignore until you are
            looking for it — which is only ever after an update. Hidden on a
            phone, where the navigation is a bottom bar and this would be
            competing for the thumb; Settings carries it there instead. */}
        <p
          title={versionTitle()}
          className="text-muted-foreground/70 pl-safe hidden px-4 pb-3 text-[11px] tabular-nums sm:mt-auto sm:block"
        >
          {formatVersion()}
        </p>
      </nav>

      <main className="pt-safe mx-auto w-full max-w-3xl flex-1 px-4 pb-24 sm:px-12 sm:pb-8">
        {children}
      </main>
    </div>
  )
}

function Badge({ count, className }: { count: number; className: string }) {
  return (
    <span
      className={`bg-primary text-primary-foreground grid size-4 place-content-center rounded-full text-[10px] font-semibold tabular-nums ${className}`}
      aria-label={`${count} unsorted`}
    >
      {count}
    </span>
  )
}
